import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { ProductAnalysisEntity } from '@/entities/product-analysis.entity';
import {
    buildFilteredProductAnalysisByTimeWindow,
    buildProductAnalysisReviewTimestampMap,
    buildProductAnalysisReviewsTimelinePoints,
    buildProductAnalysisSelectedWindowEndTimestamp,
    type ProductAnalysisTimeWindow,
    type ProductAnalysisTimelineBounds,
    buildProductAnalysisTimeWindow,
    buildProductAnalysisTimeline,
    buildProductAnalysisTimelineBounds,
    clampProductAnalysisValue,
    filterProductAnalysisReviewTimelineByTimeWindow,
    normalizeProductAnalysisTimelineControls,
    type ProductAnalysisReviewsTimelinePoint,
} from '@/domain/reporting/reporting-product-analysis-windowing';
import { useMemo, useEffect, useState, useCallback } from 'react';
import { toast } from '@/ui/toast';
import { useMarketplaceApi } from '@/context/marketplace.context';

type UseProductAnalysisModalControllerParams = {
    data: ProductAnalysisEntity | null;
    loading: boolean;
    error: string | null;
    productName: string;
    accessToken: string;
};

export type ProductAnalysisModalControllerBindings = {
    isExportLoading: boolean;
    timelineBounds: ProductAnalysisTimelineBounds | null;
    totalWindowDays: number;
    normalizedWindowWidthDays: number;
    maxWindowStartOffsetDays: number;
    normalizedWindowStartOffsetDays: number;
    maxTimelineAggregationWindowDays: number;
    normalizedTimelineAggregationWindowDays: number;
    selectedTimeWindow: ProductAnalysisTimeWindow | null;
    selectedWindowEndTimestamp: number | null;
    reviewsTimelinePoints: ProductAnalysisReviewsTimelinePoint[];
    filteredAnalysis: ProductAnalysisEntity | null;
    setWindowWidthDays: (value: number) => void;
    setWindowStartOffsetDays: (value: number) => void;
    setTimelineAggregationWindowDays: (value: number) => void;
    exportPdfReport: () => Promise<void>;
};

const toFiniteInteger = (value: number): number | null => {
    if (!Number.isFinite(value)) {
        return null;
    }

    return Math.round(value);
};

const productAnalysisModalLogger = createClientLogger({
    scope: 'product-analysis-modal',
});

export const useProductAnalysisModalController = ({
    data,
    loading,
    error,
    productName,
    accessToken,
}: UseProductAnalysisModalControllerParams): ProductAnalysisModalControllerBindings => {
    const { productsApi } = useMarketplaceApi();

    const [isExportLoading, setIsExportLoading] = useState(false);
    const [windowWidthDays, setWindowWidthDaysState] = useState(1);
    const [windowStartOffsetDays, setWindowStartOffsetDaysState] = useState(0);
    const [timelineAggregationWindowDays, setTimelineAggregationWindowDaysState] = useState(1);

    const reviewTimeline = useMemo(() => buildProductAnalysisTimeline(data), [data]);
    const reviewTimestampMap = useMemo(() => buildProductAnalysisReviewTimestampMap(reviewTimeline), [reviewTimeline]);
    const timelineBounds = useMemo(() => buildProductAnalysisTimelineBounds(reviewTimeline), [reviewTimeline]);

    useEffect(() => {
        if (!data) {
            setWindowWidthDaysState(1);
            setWindowStartOffsetDaysState(0);
            setTimelineAggregationWindowDaysState(1);
            return;
        }

        setWindowWidthDaysState(timelineBounds?.totalDays ?? 1);
        setWindowStartOffsetDaysState(0);
        setTimelineAggregationWindowDaysState(1);
    }, [data, timelineBounds?.totalDays]);

    const {
        totalWindowDays,
        normalizedWindowWidthDays,
        maxWindowStartOffsetDays,
        normalizedWindowStartOffsetDays,
        maxTimelineAggregationWindowDays,
        normalizedTimelineAggregationWindowDays,
    } = useMemo(
        () =>
            normalizeProductAnalysisTimelineControls({
                timelineBounds,
                windowWidthDays,
                windowStartOffsetDays,
                timelineAggregationWindowDays,
            }),
        [timelineAggregationWindowDays, timelineBounds, windowStartOffsetDays, windowWidthDays],
    );

    const selectedTimeWindow = useMemo(
        () =>
            buildProductAnalysisTimeWindow({
                timelineBounds,
                normalizedWindowWidthDays,
                normalizedWindowStartOffsetDays,
            }),
        [timelineBounds, normalizedWindowStartOffsetDays, normalizedWindowWidthDays],
    );

    const filteredReviewTimeline = useMemo(
        () =>
            filterProductAnalysisReviewTimelineByTimeWindow({
                reviewTimeline,
                selectedTimeWindow,
            }),
        [reviewTimeline, selectedTimeWindow],
    );

    const filteredReviewTimestamps = useMemo(
        () => filteredReviewTimeline.map((review) => review.timestamp),
        [filteredReviewTimeline],
    );

    const reviewsTimelinePoints = useMemo<ProductAnalysisReviewsTimelinePoint[]>(
        () =>
            buildProductAnalysisReviewsTimelinePoints({
                selectedTimeWindow,
                reviewTimestamps: filteredReviewTimestamps,
                timelineAggregationWindowDays: normalizedTimelineAggregationWindowDays,
            }),
        [filteredReviewTimestamps, normalizedTimelineAggregationWindowDays, selectedTimeWindow],
    );

    const filteredAnalysis = useMemo<ProductAnalysisEntity | null>(
        () =>
            buildFilteredProductAnalysisByTimeWindow({
                data,
                selectedTimeWindow,
                reviewTimestampMap,
                filteredReviewTimeline,
            }),
        [data, filteredReviewTimeline, reviewTimestampMap, selectedTimeWindow],
    );

    const selectedWindowEndTimestamp = useMemo(
        () =>
            buildProductAnalysisSelectedWindowEndTimestamp({
                selectedTimeWindow,
                timelineBounds,
            }),
        [selectedTimeWindow, timelineBounds],
    );

    const setWindowWidthDays = useCallback(
        (value: number) => {
            const parsedValue = toFiniteInteger(value);
            if (parsedValue === null) {
                return;
            }

            const nextWidth = clampProductAnalysisValue(parsedValue, 1, totalWindowDays);
            setWindowWidthDaysState(nextWidth);
            setWindowStartOffsetDaysState((currentValue) =>
                clampProductAnalysisValue(currentValue, 0, Math.max(0, totalWindowDays - nextWidth)),
            );
            setTimelineAggregationWindowDaysState((currentValue) =>
                clampProductAnalysisValue(currentValue, 1, Math.max(1, nextWidth)),
            );
        },
        [totalWindowDays],
    );

    const setWindowStartOffsetDays = useCallback(
        (value: number) => {
            const parsedValue = toFiniteInteger(value);
            if (parsedValue === null) {
                return;
            }

            setWindowStartOffsetDaysState(clampProductAnalysisValue(parsedValue, 0, maxWindowStartOffsetDays));
        },
        [maxWindowStartOffsetDays],
    );

    const setTimelineAggregationWindowDays = useCallback(
        (value: number) => {
            const parsedValue = toFiniteInteger(value);
            if (parsedValue === null) {
                return;
            }

            setTimelineAggregationWindowDaysState(
                clampProductAnalysisValue(parsedValue, 1, maxTimelineAggregationWindowDays),
            );
        },
        [maxTimelineAggregationWindowDays],
    );

    const exportPdfReport = useCallback(async () => {
        if (!filteredAnalysis || loading || error || isExportLoading) {
            return;
        }

        setIsExportLoading(true);

        try {
            await productsApi.downloadProductAnalysisPdf(
                {
                    productName,
                    analysis: filteredAnalysis,
                    selectedPeriodStart: selectedTimeWindow
                        ? new Date(selectedTimeWindow.startTimestamp).toISOString()
                        : null,
                    selectedPeriodEnd:
                        selectedWindowEndTimestamp === null ? null : new Date(selectedWindowEndTimestamp).toISOString(),
                    timelineAggregationWindowDays: normalizedTimelineAggregationWindowDays,
                    reviewsTimelinePoints: reviewsTimelinePoints.map((point) => ({
                        periodStart: new Date(point.startTimestamp).toISOString(),
                        periodEnd: new Date(Math.max(point.startTimestamp, point.endTimestamp - 1)).toISOString(),
                        reviewsCount: point.reviewsCount,
                    })),
                },
                { authToken: accessToken },
            );
            toast.success('PDF отчет выгружен');
        } catch (exportError) {
            logClientError({
                logger: productAnalysisModalLogger,
                message: '[client.product-analysis.pdf-export] Failed to export product analysis PDF.',
                error: exportError,
            });
            toast.error('Не удалось выгрузить PDF отчет');
        } finally {
            setIsExportLoading(false);
        }
    }, [
        accessToken,
        error,
        filteredAnalysis,
        isExportLoading,
        loading,
        normalizedTimelineAggregationWindowDays,
        productName,
        reviewsTimelinePoints,
        selectedTimeWindow,
        selectedWindowEndTimestamp,
    ]);

    return {
        isExportLoading,
        timelineBounds,
        totalWindowDays,
        normalizedWindowWidthDays,
        maxWindowStartOffsetDays,
        normalizedWindowStartOffsetDays,
        maxTimelineAggregationWindowDays,
        normalizedTimelineAggregationWindowDays,
        selectedTimeWindow,
        selectedWindowEndTimestamp,
        reviewsTimelinePoints,
        filteredAnalysis,
        setWindowWidthDays,
        setWindowStartOffsetDays,
        setTimelineAggregationWindowDays,
        exportPdfReport,
    };
};
